import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUpdateDistrictManagerProfile } from "@/api/districts/mutations";
import { useDistrictManagerProfile } from "@/api/districts/queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { DangerZoneSection } from "./danger-zone-section";

export function DistrictProfileTab() {
	const { t } = useTranslation();
	const { data, isLoading } = useDistrictManagerProfile();
	const updateProfile = useUpdateDistrictManagerProfile();

	const emptyForm = { first_name: "", last_name: "", company_name: "" };
	const [form, setForm] = useState(emptyForm);
	const [initialForm, setInitialForm] = useState(emptyForm);
	const prevDataRef = useRef<typeof data>(undefined);

	if (data && data !== prevDataRef.current) {
		prevDataRef.current = data;
		const loaded = {
			first_name: data.first_name ?? "",
			last_name: data.last_name ?? "",
			company_name: data.company_name ?? "",
		};
		setForm(loaded);
		setInitialForm(loaded);
	}

	const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

	function handleSave() {
		updateProfile.mutate(form, {
			onSuccess: () => toast.success(t("settings.profile.saved")),
			onError: () => toast.error(t("settings.profile.saveError")),
		});
	}

	if (isLoading) {
		return (
			<Card>
				<CardContent className="flex flex-col gap-4 pt-6">
					<Skeleton className="h-5 w-32" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-5 w-32" />
					<Skeleton className="h-10 w-full" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="flex flex-col gap-6 pt-6">
				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.profile.personalData")}
					</p>
					<div className="grid gap-2">
						<Label htmlFor="first-name">
							{t("settings.profile.firstName")}
						</Label>
						<Input
							id="first-name"
							autoComplete="given-name"
							name="first-name"
							value={form.first_name}
							onChange={(e) =>
								setForm((f) => ({ ...f, first_name: e.target.value }))
							}
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="last-name">{t("settings.profile.lastName")}</Label>
						<Input
							id="last-name"
							autoComplete="family-name"
							name="last-name"
							value={form.last_name}
							onChange={(e) =>
								setForm((f) => ({ ...f, last_name: e.target.value }))
							}
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="email">{t("settings.profile.email")}</Label>
						<Input
							id="email"
							autoComplete="email"
							name="email"
							value={data?.email ?? ""}
							disabled
						/>
					</div>
				</div>

				<Separator />

				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.profile.companyData")}
					</p>
					<div className="grid gap-2">
						<Label htmlFor="company-name">
							{t("settings.profile.companyName")}
						</Label>
						<Input
							id="company-name"
							autoComplete="organization"
							name="company-name"
							value={form.company_name}
							onChange={(e) =>
								setForm((f) => ({ ...f, company_name: e.target.value }))
							}
						/>
					</div>
				</div>

				<div className="flex justify-end gap-2">
					<Button
						size="sm"
						variant="outline"
						onClick={() => setForm(initialForm)}
						disabled={!isDirty || updateProfile.isPending}
					>
						{t("settings.profile.cancel")}
					</Button>
					<Button
						size="sm"
						onClick={handleSave}
						disabled={updateProfile.isPending || !isDirty}
					>
						{updateProfile.isPending
							? t("settings.profile.saving")
							: t("settings.profile.save")}
					</Button>
				</div>

				<Separator />

				<DangerZoneSection />
			</CardContent>
		</Card>
	);
}
